import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Edit3, Trash2, X, ArrowRight, Search } from "lucide-react";

/**
 * AdminTeams — CRUD for internal team workspaces. Each team is stored as a
 * `guidance_requests` row with type="team" so it inherits chat / files /
 * meetings / kanban / calls from the existing GuidanceWorkspace UI. Any
 * meeting scheduled inside the workspace is automatically visible to every
 * participant, satisfying the "meeting links must sync across all members"
 * requirement without a new sync layer.
 */
export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/teams").then((r) => setTeams(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  // Employees calling /admin/users get 403; fall back to /employee/assignable
  // which is scoped to any staff member (returns only staff-role users).
  useEffect(() => {
    api.get("/admin/users?role=&q=&page=1&page_size=500")
      .then((r) => setUsers((r.data?.items || r.data || []).filter(u => u.role !== "client")))
      .catch(() => {
        api.get("/employee/assignable")
          .then((r) => setUsers((r.data || []).filter(u => u.role !== "client")))
          .catch(() => {});
      });
  }, []);

  const openNew = () => setEditing({ id: null, name: "", description: "", member_user_ids: [] });
  const openEdit = (t) => setEditing({
    id: t.id, name: t.team_name || "", description: t.problem_description || "",
    member_user_ids: t.participants || [],
  });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Team name required"); return; }
    const payload = {
      name: editing.name.trim(),
      description: editing.description || "",
      member_user_ids: editing.member_user_ids || [],
      is_temporary: true,
    };
    try {
      if (editing.id) await api.put(`/admin/teams/${editing.id}`, payload);
      else await api.post("/admin/teams", payload);
      toast.success("Team saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete team "${t.team_name}"? All chat, files and meetings will be lost.`)) return;
    try { await api.delete(`/admin/teams/${t.id}`); toast.success("Team deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleMember = (uid) => setEditing((e) => ({
    ...e,
    member_user_ids: e.member_user_ids.includes(uid)
      ? e.member_user_ids.filter((x) => x !== uid)
      : [...e.member_user_ids, uid],
  }));

  const filtered = users.filter((u) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (u.name || "").toLowerCase().includes(t) || (u.email || "").toLowerCase().includes(t);
  });

  return (
    <div data-testid="admin-teams-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADMIN · WORKSPACES</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Project <em className="italic text-kb-primary">teams</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Ad-hoc, <strong>temporary</strong> project teams — Research, Marketing, Design, Interns or Custom. Each team gets a full workspace (chat, files, meetings, kanban) with meeting links auto-synced across every member. For permanent reporting lines, use <Link to="/admin/org" className="text-kb-primary underline">Org Units</Link>.</p>
        </div>
        <button onClick={openNew} className="kb-btn-primary text-sm" data-testid="admin-team-new-btn">
          <Plus className="w-4 h-4" /> New team
        </button>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        teams.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white">
            <Users className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-kb-ink-muted">No teams yet. Create the first internal workspace.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {teams.map((t) => (
              <div key={t.id} className="kb-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-team-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-medium text-kb-ink">{t.team_name || "Untitled team"}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                    {(t.participants || []).length} member{(t.participants || []).length === 1 ? "" : "s"} · created {new Date(t.created_at).toLocaleDateString()}
                  </div>
                  {t.problem_description && t.problem_description !== t.team_name && (
                    <div className="text-xs text-kb-ink-muted mt-1 line-clamp-2">{t.problem_description}</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Link to={`/dashboard/teams/${t.id}`} className="kb-btn-outline text-xs" data-testid={`admin-team-${t.id}-open`}>
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button onClick={() => openEdit(t)} className="p-2 text-kb-ink-muted hover:text-kb-primary" data-testid={`admin-team-${t.id}-edit`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(t)} className="p-2 text-rose-500 hover:text-rose-700" data-testid={`admin-team-${t.id}-delete`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      {editing && (
        <div className="fixed inset-0 z-[160] bg-black/40 flex items-center justify-center p-4" data-testid="admin-team-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full border border-slate-200 max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-serif text-lg text-kb-ink">{editing.id ? "Edit team" : "Create team"}</h3>
              <button onClick={() => setEditing(null)} className="p-1 text-kb-ink-muted hover:text-kb-ink"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Team name *</label>
                <input className="kb-input mt-1" placeholder="Research / Marketing / Design / Interns / Custom" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-team-name" />
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Short description</label>
                <textarea rows={2} className="kb-input mt-1" placeholder="What this team collaborates on" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="admin-team-desc" />
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Members ({editing.member_user_ids.length})</label>
                <div className="relative mt-1">
                  <Search className="w-3.5 h-3.5 text-kb-ink-light absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input className="kb-input pl-8" placeholder="Search staff by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-team-search" />
                </div>
                <div className="mt-2 border border-slate-200 rounded-sm max-h-56 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <div className="text-xs text-kb-ink-muted text-center py-4">No staff match.</div>
                  ) : filtered.map((u) => {
                    const on = editing.member_user_ids.includes(u.user_id);
                    return (
                      <button
                        key={u.user_id}
                        type="button"
                        onClick={() => toggleMember(u.user_id)}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between text-sm border-b border-slate-100 last:border-0 ${on ? "bg-kb-primary/5" : ""}`}
                        data-testid={`admin-team-member-${u.user_id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-kb-ink truncate">{u.name || u.email}</div>
                          <div className="kb-mono text-[10px] text-kb-ink-muted truncate">{u.email} · {u.role}{u.employee_role ? ` · ${u.employee_role}` : ""}</div>
                        </div>
                        <span className={`kb-mono text-[10px] uppercase tracking-wider ${on ? "text-kb-primary" : "text-kb-ink-light"}`}>{on ? "Member" : "Add"}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="kb-btn-outline text-xs">Cancel</button>
              <button onClick={save} className="kb-btn-primary text-xs" data-testid="admin-team-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
